import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { PopupListItem, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { TourId } from 'webapp/enums/tour';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import ClientsPromoBannerComponent from 'webapp/components/clients/promo-banner';
import ClientsTableRowComponent from 'webapp/components/clients/table-row';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonBatchTableHeaderComponent from 'webapp/components/common/batch-table-header';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import TableSorterComponent from 'webapp/components/table-sorter';
import TableAddNewItemComponent from 'webapp/components/table/add-new-item';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { AddTagAction, DeleteAction } from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import FilterSortClient from 'webapp/models-custom/filter-sort/client';
import type Space from 'webapp/models-custom/space';
import type ClientsSetupTour from 'webapp/models-custom/tour/clients-setup';

export interface Args {
	model: Space[];
	onAdd: () => void;
	onDelete: (model: Space) => void;
}

type Action = AddTagAction | DeleteAction;

export default class ClientsTableComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('event-manager') declare eventManager: Service.EventManager;
	@service('popup') declare popupService: Service.Popup;
	@service('tour') declare tourService: Service.Tour;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;

	filterSort: FilterSortClient;
	batchSelect: BatchSelect<Space>;
	addButtonText = 'Add account';
	tourClientsSetup: ClientsSetupTour | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.filterSort = new FilterSortClient({
			getAllItems: () => {
				return this.args.model;
			},
		});

		this.batchSelect = new BatchSelect({
			getAllItems: () => {
				return this.filterSort.results;
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as Action);
			},
		});
	}

	get addButtonContainerNode() {
		return isAlive(this) && !this.selectedItems.length && this.accountService.canAddClient
			? this.navigationService.addButtonContainerNode
			: null;
	}

	get batchButtonContainerNode() {
		return isAlive(this) && this.selectedItems.length
			? this.navigationService.batchButtonContainerNode
			: null;
	}

	get selectedItems(): Space[] {
		return this.batchSelect.selectedItems;
	}

	get batchOptions(): PopupListItem<Action>[] {
		const batchSelect = this.batchSelect;
		return [batchSelect.addTagsOption, batchSelect.deleteOption];
	}

	doBatchAction(actionName: Action) {
		return this[actionName]();
	}

	startTour() {
		const tour = this.tourService.initTour(TourId.CLIENTS_SETUP);
		tour?.start();
		this.tourClientsSetup = tour;
	}

	checkTour() {
		// must make sure any other popups are not open
		later(() => {
			if (
				isAlive(this) &&
				this.args.model.length &&
				!this.tourClientsSetup &&
				!this.popupService.anyPopupsOpen &&
				this.tourService.isEligibleForClientsSetupTour
			) {
				this.startTour();
			}
		}, 1000);
	}

	@action
	createClient() {
		this.args.onAdd();
	}

	@action
	deleteClient(client: Space) {
		this.args.onDelete(client);
	}

	@action
	batchDelete() {
		return this.mmSpacesService.batchDelete(this.selectedItems);
	}

	@action
	batchUpdateTags() {
		return this.mmSpacesService.batchUpdateTags(this.selectedItems);
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	setupView() {
		later(() => {
			if (!isAlive(this)) {
				return;
			}

			if (this.popupService.anyPopupsOpen) {
				this.eventManager.on(AppEvent.ALL_POPUPS_CLOSED, this, () => {
					this.checkTour();
					return true; // just once, unbind after
				});
			} else {
				this.checkTour();
			}
		}, 1000);
	}

	@action
	teardownView() {
		this.eventManager.off(AppEvent.ALL_POPUPS_CLOSED, this);

		const tour = this.tourClientsSetup;
		if (tour) {
			tour.end();
		}
	}
	<template>
		<ClientsPromoBannerComponent />

		<div class="bottom-padding">

			<FilterLineProxyComponent @model={{this.filterSort}} />

			<FilterNoResultsComponent @model={{this.filterSort}} />

			<table
				class="flexible-table has-avatar bigtable has-checkbox
					{{if this.filterSort.noResults 'hide'}}"
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange this.filterSort.results}}
				{{didInsert this.setupView}}
				{{willDestroy this.teardownView}}
			>
				<colgroup>
					<col class="cc" />
					<col class="h0" />
					<col />
					<col width="12%" />
					<col width="7%" />
					<col width="7%" />
					<col width="7%" />
					<col width="7%" />
					<col class="c8" width="15%" />
					<col class="c9" width="10%" />
					<col class="button-ct" />
				</colgroup>
				<thead>
					<tr>
						<CommonBatchTableHeaderComponent @batchSelect={{this.batchSelect}} />
						<th class="h0">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="companyName"
								@name="Name"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th></th>
						<th>
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="accountManager.name"
								@name="Account manager"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="prio-low">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="userCount"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							>
								<span class="icon">
									<CommonUiIconComponent @name="Team" @size="sm" />
									<ToolTipComponent @text="Users" />
								</span>
							</TableSorterComponent>
						</th>
						<th class="prio-low">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="sourceCount"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							>
								<span class="icon">
									<CommonUiIconComponent @name="DataManager" @size="sm" />
									<ToolTipComponent @text="Data sources" />
								</span>
							</TableSorterComponent>
						</th>
						<th class="prio-low">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="datasetCount"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							>
								<span class="icon">
									<CommonUiIconComponent @name="Table" @size="sm" />
									<ToolTipComponent @text="Datasets" />
								</span>
							</TableSorterComponent>
						</th>
						<th class="prio-low">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="boardCount"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							>
								<span class="icon">
									<CommonUiIconComponent @name="Databoards" @size="sm" />
									<ToolTipComponent @text="Dashboards" />
								</span>
							</TableSorterComponent>
						</th>
						<th>
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="billingInfo"
								@name="Subscription"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="c9 prio-low">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="tagsCount"
								@name="Tags"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="button-ct"></th>
					</tr>
				</thead>
				<tbody>
					{{#each this.filterSort.results as |result|}}
						<ClientsTableRowComponent
							@model={{result}}
							@batchSelect={{this.batchSelect}}
							@onDelete={{this.deleteClient}}
						/>
					{{/each}}
				</tbody>
			</table>
			{{#if this.accountService.canAddClient}}
				<TableAddNewItemComponent
					@title={{this.addButtonText}}
					@addNewItem={{this.createClient}}
					@model={{this.filterSort}}
				/>
			{{/if}}

		</div>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent
					@text={{this.addButtonText}}
					@callTrigger={{this.createClient}}
				/>
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
